// VisuallyHidden — screen-reader-only text, pure.
//
// Renders content that is removed from the visual layer but stays in the
// accessibility tree: an assistive-tech-only label, a status announcement,
// or the spelled-out meaning behind an icon (the "colour is never the sole
// channel" floor is often paid here). It produces NO visible output.
//
// The look is entirely the sr-only clip recipe in the CSS; there are no
// visual variants to configure, so there is no variant/size union. The one
// choice is the host element, because the correct wrapper depends on the
// caller's surrounding markup: `span` (default) is phrasing content and is
// legal inside a <p>, a <button>, or a label; `div` is flow content for
// when the hidden text sits between block siblings. That is a genuine
// structural choice, so it is a closed union of tag names — not a boolean.
//
// Pure/props-driven: no state, no effects. `children` is the hidden text;
// every other attribute (id, role, aria-live…) passes straight through to
// the chosen element.

import type { HTMLAttributes } from "react";
import "./VisuallyHidden.css";

export type VisuallyHiddenElement = "span" | "div" | "h1";

export type VisuallyHiddenProps = HTMLAttributes<HTMLElement> & {
    readonly as?: VisuallyHiddenElement;
};

export const VisuallyHidden = ({
    as = "span",
    className,
    ...rest
}: VisuallyHiddenProps) => {
    const cn = `bilda-visually-hidden${className ? ` ${className}` : ""}`;
    // Branch per tag rather than assigning to a dynamic `Tag` — keeps the
    // element type concrete and avoids any cast on the intrinsic element.
    return as === "div" ? (
        <div className={cn} {...rest} />
    ) : as === "h1" ? (
        <h1 className={cn} {...rest} />
    ) : (
        <span className={cn} {...rest} />
    );
};
